import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, ShieldCheck, Zap, Clock,
  ChevronRight, Users, Star, ArrowRight,
  Menu, X, Sparkles
} from 'lucide-react';
// 1. IMPORT TRANSLATION HOOK
import { useTranslation } from 'react-i18next';

const LandingPage = () => {
  // 2. INITIALIZE HOOK
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // 🚀 FIX: Force scroll to top on mount and ensure body is scrollable
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return (
    /* 🚀 FIX: Changed to min-h-screen and removed overflow-y-auto here 
       to let the browser handle natural scrolling at the body level. */
    <div className="w-full bg-white font-sans text-slate-900 selection:bg-blue-100">

      {/* --- Navigation --- */}
      <nav className="sticky top-0 z-[100] bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1.5 md:p-2 rounded-lg text-white shadow-lg shadow-blue-200">
              <BookOpen size={20} />
            </div>
            <span className="text-lg md:text-xl font-black tracking-tighter">{t('app_name', 'AtronsTutor')}</span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-[10px] font-black uppercase tracking-widest text-slate-500">
            <a href="#how-it-works" className="hover:text-blue-600 transition-colors">{t('how_it_works', 'How it works')}</a>
            <a href="#features" className="hover:text-blue-600 transition-colors">{t('features', 'Features')}</a>
            <Link to="/login" className="text-slate-900 hover:text-blue-600 transition-colors">{t('sign_in', 'Sign In')}</Link>
            <Link to="/signup" className="bg-slate-900 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition-all shadow-xl shadow-slate-200">{t('get_started', 'Get Started')}</Link>
          </div>

          <button className="md:hidden p-2 text-slate-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-b border-slate-100 p-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-top-5 duration-300">
            <a href="#how-it-works" onClick={() => setIsMenuOpen(false)} className="font-bold text-slate-600">{t('how_it_works', 'How it works')}</a>
            <a href="#features" onClick={() => setIsMenuOpen(false)} className="font-bold text-slate-600">{t('features', 'Features')}</a>
            <div className="h-px bg-slate-100 my-2" />
            <Link to="/login" onClick={() => setIsMenuOpen(false)} className="font-bold text-slate-600">{t('sign_in', 'Sign In')}</Link>
            <Link to="/signup" onClick={() => setIsMenuOpen(false)} className="bg-blue-600 text-white text-center py-4 rounded-2xl font-black uppercase tracking-widest text-[10px]">{t('get_started', 'Get Started')}</Link>
          </div>
        )}
      </nav>

      {/* --- Hero Section --- */}
      <header className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 lg:py-24 grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="text-center lg:text-left order-1">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest mb-6">
            <Star size={12} fill="currentColor" /> {t('first_15_free', 'First 15 Minutes Free')}
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.1] mb-6 tracking-tighter">
            {t('landing_hero_title_1', 'Master Any')} <span className="text-blue-600">{t('landing_hero_title_2', 'Subject')}</span> <br className="hidden md:block" /> {t('landing_hero_title_3', 'From Home.')}
          </h1>
          <p className="text-base md:text-lg text-slate-500 mb-8 md:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            <b className="text-slate-900">{t('landing_hero_subtitle_1', 'Real-time billing and interactive whiteboards.')}</b><br className="hidden md:block"/>
            {t('landing_hero_subtitle_2', 'Connect with verified University tutors in Ethiopia.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start px-4 md:px-0">
            <Link to="/signup" className="bg-blue-600 text-white px-8 py-4 md:py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 shadow-2xl shadow-blue-200 transition-all active:scale-95">
              {t('get_started_now', 'Get Started Now')} <ChevronRight size={16} />
            </Link>
          </div>
        </div>

        <div className="relative order-2 px-4 md:px-0">
          <div className="absolute -inset-2 md:-inset-4 bg-blue-50 rounded-[2rem] md:rounded-[2.5rem] rotate-2 -z-10 animate-pulse"></div>
          <img src="/Tutors2.avif" alt="Tutors" className="rounded-[1.5rem] md:rounded-[2rem] shadow-2xl border-4 border-white w-full object-cover" />
        </div>
      </header>

      {/* --- Process Section --- */}
      <section id="how-it-works" className="bg-slate-50 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter mb-4">{t('simple_3_step_process', 'Simple 3-Step Process')}</h2>
          <div className="h-1.5 w-16 bg-blue-600 mx-auto rounded-full mb-16"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 text-left">
            {[
              { step: "01", title: t('step_1_title', "Post a Request"), desc: t('step_1_desc', "Tell us your subject and what you need help with.") },
              { step: "02", title: t('step_2_title', "Get Matched"), desc: t('step_2_desc', "Our Admins pair you with the best verified tutor.") },
              { step: "03", title: t('step_3_title', "Start Class"), desc: t('step_3_desc', "Join the live room. First 15 minutes are free!") },
            ].map((item, idx) => (
              <div key={idx} className="relative p-8 bg-white rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-shadow group">
                <span className="text-4xl md:text-5xl font-black text-blue-50 block mb-6 group-hover:text-blue-100 transition-colors">{item.step}</span>
                <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Features --- */}
      <section id="features" className="max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
        <div className="grid grid-cols-2 gap-4 md:gap-6 order-2 lg:order-1">
          <div className="bg-blue-600 text-white p-6 md:p-10 rounded-[2rem] shadow-xl flex flex-col justify-between aspect-square">
            <ShieldCheck size={32} />
            <p className="text-sm md:text-base font-black uppercase tracking-tighter leading-tight">{t('verified_tutors', '100% Verified Tutors')}</p>
          </div>
          <div className="bg-slate-100 text-slate-900 p-6 md:p-10 rounded-[2rem] flex flex-col justify-between aspect-square mt-6 md:mt-12">
            <Zap size={32} className="text-blue-600" />
            <p className="text-sm md:text-base font-black uppercase tracking-tighter leading-tight">{t('instant_billing', 'Instant Billing')}</p>
          </div>
          <div className="bg-slate-900 text-white p-6 md:p-10 rounded-[2rem] flex flex-col justify-between aspect-square -mt-6 md:-mt-12">
            <Clock size={32} className="text-blue-400" />
            <p className="text-sm md:text-base font-black uppercase tracking-tighter leading-tight">{t('pay_per_second', 'Pay Per Second')}</p>
          </div>
          <div className="bg-blue-50 text-blue-900 p-6 md:p-10 rounded-[2rem] flex flex-col justify-between aspect-square">
            <Users size={32} />
            <p className="text-sm md:text-base font-black uppercase tracking-tighter leading-tight">{t('expert_support', 'Expert Support')}</p>
          </div>
        </div>

        <div className="order-1 lg:order-2 space-y-8">
          <div className="inline-flex items-center gap-2 text-blue-600 font-bold uppercase text-[10px] tracking-widest">
            <Sparkles size={14} /> {t('why_choose_us', 'Why choose us')}
          </div>
          <h2 className="text-3xl md:text-5xl font-black mb-8 leading-tight tracking-tighter text-slate-900">
            {t('features_title_1', 'University Experts at your')} <span className="text-blue-600">{t('features_title_2', 'fingertips')}</span>.
          </h2>
          <ul className="space-y-4">
            {[
              t('feature_list_1', 'National Exam Mastery (Grade 12)'), 
              t('feature_list_2', 'Daily Homework Solutions'), 
              t('feature_list_3', 'Specialized University Courses')
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-4 font-bold text-slate-700 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <div className="bg-blue-600 text-white p-1 rounded-md shrink-0"><ArrowRight size={14} /></div>
                <span className="text-sm md:text-base">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* --- CTA Footer --- */}
      <footer className="bg-slate-950 text-white py-20 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-6 tracking-tighter">{t('cta_title', 'Ready to boost your grades?')}</h2>
          <p className="text-slate-400 mb-10 font-medium">{t('cta_desc', 'Join thousands of Ethiopian students mastering their education with university experts.')}</p>
          <Link to="/signup" className="inline-flex bg-blue-600 px-10 py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-500 transition-all hover:scale-105 shadow-2xl shadow-blue-500/20 active:scale-95">
            {t('create_free_account', 'Create Free Account')}
          </Link>
          <div className="mt-16 pt-8 border-t border-slate-900 text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em]">
            © {new Date().getFullYear()} {t('app_name', 'AtronsTutor')} Ethiopia
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;